import {
  IS_BOLD,
  IS_CODE,
  IS_HIGHLIGHT,
  IS_ITALIC,
  IS_STRIKETHROUGH,
  IS_SUBSCRIPT,
  IS_SUPERSCRIPT,
  IS_UNDERLINE,
  addExportVisitor$,
  addImportVisitor$,
  realmPlugin,
  type LexicalExportVisitor,
  type MdastImportVisitor,
} from '@mdxeditor/editor'
import type { TextDirective } from 'mdast-util-directive'

/**
 * Sublinhado no dialeto (T40): `:u[texto]`.
 *
 * No editor, sublinhar é o formato nativo do Lexical (botão "S" da barra, Cmd/Ctrl+U), como negrito
 * e itálico. O MDXEditor gravaria `<u>` em HTML, que o dialeto não aceita; estes dois visitantes
 * fazem a ida e a volta pela diretiva de texto `u` do `@westhetics/legal`, com prioridade sobre os
 * visitantes padrão. Vale também dentro dos blocos e das células de tabela, que usam os mesmos.
 */

/** O tipo do nó do Lexical, tirado da assinatura do MDXEditor: `lexical` não é dependência daqui. */
type AnyLexicalNode = Parameters<
  NonNullable<LexicalExportVisitor<never, never>['testLexicalNode']>
>[0]

interface UnderlinedTextNode extends AnyLexicalNode {
  getFormat(): number
  getTextContent(): string
}

interface MdastLike {
  type: string
  name?: string | null
  children?: MdastLike[]
}

type Append = (parent: unknown, node: unknown) => unknown

const isUnderlinedText = (node: AnyLexicalNode): node is UnderlinedTextNode =>
  node.getType() === 'text' && ((node as UnderlinedTextNode).getFormat() & IS_UNDERLINE) !== 0

const isUnderline = (node: MdastLike) => node.type === 'textDirective' && node.name === 'u'

/** Lexical → mdast: o texto sublinhado vira `:u[…]`, com os outros formatos aninhados dentro. */
const underlineExportVisitor: LexicalExportVisitor<UnderlinedTextNode, TextDirective> = {
  priority: 1,
  testLexicalNode: isUnderlinedText,
  // Dois trechos sublinhados seguidos (um deles também em negrito, por exemplo) ficam num :u[] só.
  shouldJoin: (prev, current) => isUnderline(prev as MdastLike) && isUnderline(current),
  join: (prev, current) =>
    ({
      ...prev,
      children: [
        ...((prev as MdastLike).children ?? []),
        ...((current as MdastLike).children ?? []),
      ],
    }) as typeof prev,
  visitLexicalNode: ({ lexicalNode, mdastParent, actions }) => {
    const append = actions.appendToParent as unknown as Append
    const format = lexicalNode.getFormat()
    const value = lexicalNode.getTextContent()
    const directive: TextDirective = {
      type: 'textDirective',
      name: 'u',
      attributes: {},
      children: [],
    }
    let parent = append(mdastParent, directive)
    // A mesma ordem de aninhamento do visitante de texto do MDXEditor.
    const wrappers: Array<[number, MdastLike]> = [
      [IS_SUPERSCRIPT, { type: 'mdxJsxTextElement', name: 'sup', children: [] }],
      [IS_SUBSCRIPT, { type: 'mdxJsxTextElement', name: 'sub', children: [] }],
      [IS_ITALIC, { type: 'emphasis', children: [] }],
      [IS_BOLD, { type: 'strong', children: [] }],
      [IS_STRIKETHROUGH, { type: 'delete', children: [] }],
      [IS_HIGHLIGHT, { type: 'highlight', children: [] }],
    ]
    for (const [flag, wrapper] of wrappers) {
      if (format & flag) {
        parent = append(
          parent,
          wrapper.type === 'mdxJsxTextElement' ? { ...wrapper, attributes: [] } : wrapper,
        )
      }
    }
    if (format & IS_CODE) append(parent, { type: 'inlineCode', value })
    else append(parent, { type: 'text', value })
  },
}

/** mdast → Lexical: `:u[…]` vira texto com o formato sublinhado. */
const underlineImportVisitor: MdastImportVisitor<TextDirective> = {
  priority: 1,
  testNode: (node) => isUnderline(node as MdastLike),
  visitNode: ({ mdastNode, actions, lexicalParent }) => {
    actions.addFormatting(IS_UNDERLINE)
    actions.visitChildren(mdastNode, lexicalParent)
  },
}

export const underlinePlugin = realmPlugin({
  init: (realm) => {
    realm.pubIn({
      [addImportVisitor$]: underlineImportVisitor,
      [addExportVisitor$]: underlineExportVisitor,
    })
  },
})
